import React, { useState, useEffect } from 'react';
import { ChevronLeft, Loader2, Flame, Calendar, RotateCw, TrendingUp } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useClientAuth } from '../../../context/ClientAuthContext';

const StatCard = ({ icon: Icon, label, value, color = 'text-orange-400' }) => (
  <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4">
    <Icon className={`w-5 h-5 mb-2 ${color}`} />
    <p className="text-2xl font-black text-white">{value}</p>
    <p className="text-xs text-zinc-500">{label}</p>
  </div>
);

/** Historique cardio global : tours complétés + calories estimées, séance après séance. */
export const CardioStats = ({ en, onBack }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [totals, setTotals] = useState({ sessions_count: 0, total_rounds: 0, total_calories: 0 });

  useEffect(() => {
    authFetch('/api/tracking/cardio-pro/stats/history').then((r) => r.json()).then((d) => {
      setTimeline((d.timeline || []).map((t, i) => ({ ...t, index: i + 1 })));
      setTotals(d.totals || {});
    }).finally(() => setLoading(false));
  }, [authFetch]);

  return (
    <div className="space-y-5" data-testid="cardio-stats">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="cardio-stats-back-btn">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <h2 className="text-xl font-black text-white flex items-center gap-2"><TrendingUp className="w-5 h-5 text-orange-400" /> {L('Progression Cardio', 'Cardio Progress')}</h2>

      {loading ? (
        <div className="flex justify-center py-14"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
      ) : timeline.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.035] border border-white/10 p-8 text-center text-zinc-500 text-sm" data-testid="cardio-stats-empty">
          {L('Terminez votre première séance cardio pour voir vos statistiques ici.', 'Complete your first cardio session to see your stats here.')}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <StatCard icon={Calendar} label={L('Séances', 'Sessions')} value={totals.sessions_count} />
            <StatCard icon={RotateCw} label={L('Tours AMRAP cumulés', 'Total AMRAP rounds')} value={totals.total_rounds} color="text-violet-400" />
            <StatCard icon={Flame} label={L('Calories estimées', 'Estimated calories')} value={`${totals.total_calories} kcal`} />
          </div>

          <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4" data-testid="cardio-rounds-chart">
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{L('Tours complétés par séance', 'Rounds completed per session')}</p>
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="roundsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f97316" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="index" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }} formatter={(v) => [v, L('tours', 'rounds')]} />
                <Area type="monotone" dataKey="rounds" stroke="#f97316" strokeWidth={2} fill="url(#roundsGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white/[0.035] backdrop-blur-xl border border-white/10 p-4" data-testid="cardio-calories-chart">
            <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-3">{L('Calories estimées par séance', 'Estimated calories per session')}</p>
            <ResponsiveContainer width="100%" height={150}>
              <AreaChart data={timeline}>
                <defs>
                  <linearGradient id="calGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="index" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} width={30} />
                <Tooltip contentStyle={{ background: '#18181b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, fontSize: 12 }} formatter={(v) => [`${v} kcal`, L('calories', 'calories')]} />
                <Area type="monotone" dataKey="calories" stroke="#22d3ee" strokeWidth={2} fill="url(#calGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
};

export default CardioStats;
